import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";
import { StockailySymbol } from "@/components/brand/StockailyBrand";
import { safeNextPath, stashGoogleReturnPath } from "@/lib/safeNext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogle(nextPath) {
  // Slice 1 (2026-02-14) — preserve the intended post-auth
  // destination through the Google OAuth redirect.  Only validated
  // internal paths (see safeNext.js) survive this stash; anything
  // else is silently dropped and the callback falls back to
  // /watchlist.  This is how ticker preservation survives Google
  // sign-in from the anonymous analyze gate.
  stashGoogleReturnPath(nextPath);
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { user, loading: authLoading, loginWithCredentials, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [oauthErrorSuppressed, setOauthErrorSuppressed] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search || "");
  const oauthError = params.get("error");
  const next = safeNextPath(new URLSearchParams(loc.search).get("next"));

  // Slice 1 fix — false-positive Google login failure.
  // -----------------------------------------------------------------
  // The previous code showed the failure toast as soon as
  // `error=google` was present + `!user`.  In production the
  // AuthCallback's `/auth/google/session` POST is occasionally
  // classified as failed even though the HttpOnly `session_token`
  // cookie was actually written server-side (double-submit CSRF
  // race, response interceptor SPA-fallback guard, transient 500).
  // The user then saw a red "Google login failed" toast before
  // AuthContext's own /auth/me probe had a chance to hydrate `user`.
  //
  // New rule: when `error=google` is present, we FIRST re-verify
  // the authoritative session via checkAuth().  If /auth/me returns
  // 200, the session IS valid — we silently strip the stale error
  // param and redirect to the intended destination.  Only if the
  // re-verified state is definitively unauthed do we show the
  // failure toast.  No arbitrary delays; no global error hiding.
  useEffect(() => {
    if (oauthError !== "google") return;
    let cancelled = false;
    (async () => {
      try {
        if (typeof checkAuth === "function") {
          await checkAuth();
        }
      } catch {
        /* swallow — treated as unauthed below */
      }
      if (cancelled) return;
      setOauthErrorSuppressed(true);
    })();
    return () => { cancelled = true; };
  }, [oauthError, checkAuth]);

  // If the re-verified session hydrated `user`, silently strip the
  // stale error param and forward to `next`.  Nothing on-screen ever
  // shows the misleading failure copy.
  useEffect(() => {
    if (user && oauthError === "google") {
      const p = new URLSearchParams(loc.search || "");
      p.delete("error");
      const s = p.toString();
      const clean = window.location.pathname + (s ? `?${s}` : "");
      window.history.replaceState(null, "", clean);
      navigate(next, { replace: true });
    }
  }, [user, oauthError, loc.search, navigate, next]);

  // Only show the OAuth failure toast when the re-verified state is
  // definitively unauthed (checkAuth finished, still no user).  The
  // `oauthErrorSuppressed` flag ensures the toast never fires before
  // checkAuth has had a chance to resolve.
  useEffect(() => {
    if (oauthError !== "google") return;
    if (authLoading || !oauthErrorSuppressed) return;
    if (user) return; // success path handled above
    toast.error(t("auth.google_failed") || "Google sign-in failed. Please try again.");
  }, [oauthError, oauthErrorSuppressed, authLoading, user, t]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginWithCredentials(email.trim(), password);
      toast.success(t("auth.welcome_toast"));
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.bad_creds"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="hidden lg:block">
            <div className="font-display text-3xl tracking-tight">{t("auth.welcome_back")}</div>
            <p className="text-muted-foreground mt-2 max-w-md">{t("auth.welcome_back_sub")}</p>
          </div>
          <div className="max-w-md w-full mx-auto rounded-2xl border border-border/70 bg-card/60 p-6 sm:p-7 shadow-[var(--app-shadow-elev-2)]" data-testid="login-card">
            <div className="flex justify-center mb-4">
              <StockailySymbol height={52} decorative className="sm:!h-[52px]" />
            </div>
            <h1 className="font-display text-2xl text-center">{t("auth.login_title")}</h1>
            <p className="text-sm text-muted-foreground mt-1 text-center">{t("auth.login_subtitle")}</p>
            <Button variant="secondary" className="w-full mt-5 h-11" onClick={() => startGoogle(next)} data-testid="login-google-button">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" fill="currentColor"><path d="M44.5 20H24v8.5h11.7C34.7 33 30.1 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l6.1-6.1C34.4 5 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.4-.2-2.7-.5-4z"/></svg>
              {t("auth.google")}
            </Button>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border/70" /> {t("common.or")} <div className="flex-1 h-px bg-border/70" />
            </div>
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <Label htmlFor="login-email">{t("auth.email_label")}</Label>
                <Input id="login-email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" placeholder={t("auth.email_placeholder")} className="mt-1 h-11" />
              </div>
              <div>
                <Label htmlFor="login-password">{t("auth.password_label")}</Label>
                <Input id="login-password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" placeholder={t("auth.password_placeholder")} className="mt-1 h-11" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11" data-testid="login-submit-button">{busy ? t("auth.signing_in") : t("auth.log_in")}</Button>
            </form>
            <div className="mt-3 text-sm text-right">
              <Link
                to="/forgot-password"
                className="text-muted-foreground hover:text-foreground hover:underline"
                data-testid="login-forgot-password-link"
              >
                {t("auth.forgot_password_link")}
              </Link>
            </div>
            <div className="mt-4 text-sm text-muted-foreground">
              {t("auth.no_account")}{" "}
              <Link
                to={`/signup${next && next !== "/watchlist" ? `?next=${encodeURIComponent(next)}` : ""}`}
                className="text-foreground hover:underline"
                data-testid="login-go-signup"
              >
                {t("auth.create_one")}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
